import { useState, useRef, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { pathFor, type Lang } from '../../router/paths';

// ─── Data ────────────────────────────────────────────────────────────────────

const WORLDS = [
  { id: 'seen', labelKey: 'worldSeen', icon: 'ri-water-flash-line', color: 'from-sky-400 to-cyan-600', bg: 'bg-sky-50', accent: 'text-sky-700', border: 'border-sky-200', pill: 'bg-sky-100 text-sky-700' },
  { id: 'muenchen', labelKey: 'worldMuenchen', icon: 'ri-building-2-line', color: 'from-stone-500 to-stone-700', bg: 'bg-stone-50', accent: 'text-stone-700', border: 'border-stone-200', pill: 'bg-stone-100 text-stone-700' },
  { id: 'berge', labelKey: 'worldBerge', icon: 'ri-landscape-line', color: 'from-green-500 to-emerald-700', bg: 'bg-green-50', accent: 'text-green-700', border: 'border-green-200', pill: 'bg-green-100 text-green-700' },
  { id: 'kultur', labelKey: 'worldKultur', icon: 'ri-ancient-gate-line', color: 'from-amber-500 to-amber-700', bg: 'bg-amber-50', accent: 'text-amber-700', border: 'border-amber-200', pill: 'bg-amber-100 text-amber-700' },
  { id: 'gauting', labelKey: 'worldGauting', icon: 'ri-home-heart-line', color: 'from-rose-400 to-rose-600', bg: 'bg-rose-50', accent: 'text-rose-700', border: 'border-rose-200', pill: 'bg-rose-100 text-rose-700' },
];

// IDs ohne eigenes Bild oder bewusst ausgeblendet (Wörthsee, Ruhiger Tag).
// Sobald ein Ziel ein eigenes Bild bekommt: ID hier entfernen.
const HIDDEN_EXCURSION_IDS = new Set<number>([
  27,                                           // Ruhiger Tag im Ort (keine Empfehlung)
  35, 39, 40, 41, 42, 43, 44, 45,               // Berge ohne eigenes Bild
  47, 49, 50, 51,                               // Kultur ohne eigenes Bild
  52, 53,                                       // Gauting ohne eigenes Bild
  55,                                           // Bad Bayersoien – wartet auf Bild
]);

const EXCURSIONS = [
  {
    id: 2,
    world: 'seen',
    title: 'Ammersee',
    tagline: 'Wilder, ursprünglicher, weiter',
    duration: '25 Min.',
    mood: '⛵ Segeln & Seele baumeln',
    tip: 'Tipp: Fähre von Herrsching nach Andechs – Bier und Aussicht inklusive.',
    highlights: ['Segeln', 'Fähre', 'Fischrestaurants', 'Naturschutzgebiet'],
    description: 'Etwas weniger bekannt als der Starnberger See, dafür umso ursprünglicher. Weite Ufer, Segelboote, frischer Fisch – und das Kloster Andechs thront gleich oben drüber.',
    image: '/images/ausflugsziele/Ausflugsziel_Ammersee_Paddler.jpg',
    objectPosition: 'center calc(50% - 50px)',
    mapLink: 'https://maps.google.com/?q=Ammersee+Bayern',
  },
  {
    id: 3,
    world: 'seen',
    title: 'Roseninsel besuchen',
    tagline: 'Stille Insel voller Geschichte',
    duration: '20 Min.',
    mood: '🌹 Königliche Atmosphäre',
    tip: 'Tipp: Die Überfahrt per Zille ist ein Erlebnis für sich – nur bei gutem Wetter möglich.',
    highlights: ['Rosengarten', 'Zille-Überfahrt', 'Königliche Villa', 'Ruhe'],
    description: 'Eine stille Insel voller Geschichte, Rosen und königlicher Atmosphäre – erreichbar per Zille und ideal für einen besonderen Ausflug.',
    image: '/images/ausflugsziele/Roseninsel.jpg',
    mapLink: 'https://maps.google.com/?q=Roseninsel+Starnberger+See',
  },
  {
    id: 4,
    world: 'kultur',
    title: 'Zur Votivkapelle in Berg spazieren',
    tagline: 'Geschichte am Seeufer',
    duration: '20 Min.',
    mood: '⛪ Stimmungsvoll & historisch',
    tip: 'Tipp: Das Kreuz im Wasser markiert die Stelle, wo König Ludwig II. starb – ein bewegender Ort.',
    highlights: ['Votivkapelle', 'Kreuz im Wasser', 'Parkanlage', 'Seeblick'],
    description: 'Ein stimmungsvoller Spaziergang mit Seeblick und Geschichte: zur Kapelle im Park von Berg und zum Kreuz im Wasser.',
    image: '/images/ausflugsziele/votivkapelle_neu.jpg',
    mapLink: 'https://maps.google.com/?q=Votivkapelle+Berg+Starnberger+See',
  },
  {
    id: 5,
    world: 'seen',
    title: 'Im Lenné-Park in Feldafing flanieren',
    tagline: 'Elegante Ruhe am See',
    duration: '18 Min.',
    mood: '🌳 Parkspaziergang',
    tip: 'Tipp: Der Park führt direkt zum Seeufer – perfekt für eine Kombination aus Kultur und Natur.',
    highlights: ['Historischer Park', 'Alte Bäume', 'Seeblick', 'Ruhe'],
    description: 'Alte Bäume, weite Wege und elegante Ruhe machen den Lenné-Park zu einem wunderbaren Ort für einen Spaziergang am See.',
    image: '/images/ausflugsziele/Feldafinger_Park.jpg',
    objectPosition: 'center bottom',
    credit: 'I. Berger, CC BY-SA 3.0, via Wikimedia Commons',
    creditUrl: 'https://creativecommons.org/licenses/by-sa/3.0',
    mapLink: 'https://maps.google.com/?q=Lenné-Park+Feldafing',
  },
  {
    id: 6,
    world: 'seen',
    title: 'Eine Überfahrt auf dem Starnberger See machen',
    tagline: 'Der See von seiner schönsten Seite',
    duration: '15 Min.',
    mood: '⛴️ Wasserblick',
    tip: 'Tipp: Bei klarem Wetter sieht man die Alpen vom Schiff aus besonders eindrucksvoll.',
    highlights: ['Schifffahrt', 'Villen', 'Alpenpanorama', 'Weite'],
    description: 'Vom Wasser aus zeigt sich der See besonders schön: mit Villen, Ufern, Weite und bei guter Sicht sogar mit Alpenpanorama.',
    image: '/images/ausflugsziele/starberger_see_schiff_utting.jpg',
    objectPosition: 'center bottom',
    mapLink: 'https://maps.google.com/?q=Starnberger+See+Schifffahrt',
  },
  {
    id: 7,
    world: 'seen',
    title: 'Schifffahrt über den Ammersee',
    tagline: 'Weite, Wasser und Sommer',
    duration: '25 Min.',
    mood: '🚢 Entspannt & aussichtsreich',
    tip: 'Tipp: Die Rundfahrt ab Herrsching ist besonders schön und führt an allen wichtigen Orten vorbei.',
    highlights: ['Schifffahrt', 'Weite', 'Sommerfeeling', 'Aussicht'],
    description: 'Weite, Wasser und ein Gefühl von Sommer: Eine Schifffahrt auf dem Ammersee ist entspannt, aussichtsreich und wunderbar leicht.',
    image: '/images/ausflugsziele/Schifffahrt_Ammersee.jpg',
    mapLink: 'https://maps.google.com/?q=Ammersee+Schifffahrt',
  },
  {
    id: 8,
    world: 'kultur',
    title: 'Kloster Andechs',
    tagline: 'Heiliger Berg & heiliges Bier',
    duration: '30 Min.',
    mood: '🍺 Kultur trifft Genuss',
    tip: 'Tipp: Stiftsschänke direkt am Kloster – Bier vom Fass, Blick über den Ammersee.',
    highlights: ['Klosterkirche', 'Klosterbier', 'Aussicht', 'Wallfahrt'],
    description: 'Erst ans Wasser, dann hinauf zum Kloster: Andechs verbindet Panorama, Geschichte und bayerische Einkehr auf besonders schöne Weise.',
    image: '/images/ausflugsziele/Ausflug_Andechs_Fruehlingswiese.jpg',
    objectPosition: 'center calc(50% + 50px)',
    mapLink: 'https://maps.google.com/?q=Kloster+Andechs',
  },
  {
    id: 9,
    world: 'seen',
    title: 'Baden, SUP oder einfach am Wörthsee entspannen',
    tagline: 'Klar, sommerlich und entspannt',
    duration: '12 Min.',
    mood: '🏊 Sommerfeeling',
    tip: 'Tipp: SUP-Verleih direkt am Seeufer – auch für Anfänger perfekt geeignet.',
    highlights: ['Baden', 'SUP', 'Entspannung', 'Sommer'],
    description: 'Klar, sommerlich und entspannt: Der Wörthsee ist ideal für Baden, SUP oder einfach ein paar ruhige Stunden am Wasser.',
    image: '/images/ausflugsziele/Woerthsee.jpg',
    mapLink: 'https://maps.google.com/?q=Wörthsee+Bayern',
  },
  {
    id: 10,
    world: 'seen',
    title: 'Den Wörthsee-Rundweg entlangwandern',
    tagline: 'Leichte Bewegung, stille Momente',
    duration: '12 Min.',
    mood: '🚶 Entspannter Rundweg',
    tip: 'Tipp: Der Rundweg ist etwa 8 km lang und führt durch schöne Uferlandschaften.',
    highlights: ['Rundweg', 'Seemomente', 'Natur', 'Leicht'],
    description: 'Ein schöner Weg für leichte Bewegung und stille Seemomente – perfekt für alle, die den Tag draußen entspannt angehen wollen.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Wörthsee+Rundweg',
  },
  {
    id: 1,
    world: 'seen',
    title: 'Starnberger See',
    tagline: 'Bayerns schönstes Wasser',
    duration: '15 Min.',
    mood: '🌊 Entspannung pur',
    tip: 'Tipp: Bootsverleih direkt am Ufer in Starnberg – kein Führerschein nötig!',
    highlights: ['Bootsfahrt', 'Baden', 'Uferpromenade', 'Sonnenuntergang'],
    description: 'Kristallklares Wasser, sanfte Hügel, Alpenpanorama im Hintergrund. Der Starnberger See ist in 15 Minuten erreichbar und fühlt sich an wie Urlaub im Urlaub.',
    image: '/images/ausflugsziele/Starnberger_See_neu_Koenig_Ludwig_Kreuz_im_Wasser.jpg',
    mapLink: 'https://maps.google.com/?q=Starnberger+See',
  },
  {
    id: 11,
    world: 'seen',
    title: 'Pilsensee mit Schloss Seefeld verbinden',
    tagline: 'Wasser und Kultur kombiniert',
    duration: '20 Min.',
    mood: '🏰 Landschaft & Kultur',
    tip: 'Tipp: In Seefeld gibt es kleine Läden und Cafés – perfekt für eine entspannte Pause.',
    highlights: ['Pilsensee', 'Schloss Seefeld', 'Kultur', 'Entspannung'],
    description: 'Erst ans Wasser, dann nach Schloss Seefeld: ein Ausflug mit Landschaft, kleinen Läden, Kultur und entspannter Atmosphäre.',
    image: '/images/ausflugsziele/pilsensee.jpg',
    credit: 'Carsten Steger, CC BY-SA 4.0, via Wikimedia Commons',
    creditUrl: 'https://creativecommons.org/licenses/by-sa/4.0',
    mapLink: 'https://maps.google.com/?q=Pilsensee+Schloss+Seefeld',
  },
  {
    id: 12,
    world: 'seen',
    title: 'Weßlinger See',
    tagline: 'Klein, ruhig, unkompliziert',
    duration: '8 Min.',
    mood: '🌿 Ruhige Umrundung',
    tip: 'Tipp: Der See ist klein genug für eine schnelle Runde – ideal für zwischendurch.',
    highlights: ['Kleiner See', 'Umrundung', 'Ruhe', 'Natur'],
    description: 'Klein, ruhig und wunderbar unkompliziert: Der Weßlinger See eignet sich perfekt für eine Umrundung am Wasser zwischendurch.',
    image: '/images/ausflugsziele/Ausflug_Wesslinger_See_Herbst.jpg',
    images: [
      '/images/ausflugsziele/Ausflug_Wesslinger_See_Herbst.jpg',
      '/images/ausflugsziele/Ausflug_Wesslinger_See_Abendstimmung_Boot.jpg',
      '/images/ausflugsziele/Ausflug_Wesslinger_See_Winter.jpg',
      '/images/ausflugsziele/Ausflugsziele_Wesslinger_See_Kirche.jpg',
      '/images/ausflugsziele/Ausflugsziele_Wesslinger_See_Rundweg.jpg',
    ],
    objectPosition: 'center bottom',
    mapLink: 'https://maps.google.com/?q=Weßlinger+See',
  },
  {
    id: 13,
    world: 'muenchen',
    title: 'München – Innenstadt',
    tagline: 'Weltstadt mit Herz',
    duration: '25 Min. S-Bahn',
    mood: '🏙️ Shoppen, Schlendern, Staunen',
    tip: 'Tipp: S6 ab Gauting – direkt in die Innenstadt, kein Umsteigen.',
    highlights: ['Marienplatz', 'Viktualienmarkt', 'Englischer Garten', 'Shopping'],
    description: 'Glockenspiel, Weißwurst, Weltklasse-Museen und die besten Biergärten Europas. München ist in 25 Minuten da – und fühlt sich trotzdem wie eine eigene Reise an.',
    image: '/images/ausflugsziele/muenchen_innenstadt.jpg',
    mapLink: 'https://maps.google.com/?q=Marienplatz+München',
  },
  {
    id: 14,
    world: 'muenchen',
    title: 'Marienplatz',
    tagline: 'Das Herz von München',
    duration: '25 Min. S-Bahn',
    mood: '🔔 Glockenspiel & Großstadt',
    tip: 'Tipp: Das Glockenspiel im Rathausturm spielt täglich um 11 und 12 Uhr – im Sommer zusätzlich um 17 Uhr.',
    highlights: ['Neues Rathaus', 'Glockenspiel', 'Frauenkirche', 'Fußgängerzone'],
    description: 'Mitten im Herzen der Stadt: Der Marienplatz mit Neuem Rathaus, dem berühmten Glockenspiel und der Mariensäule ist Münchens lebendiger Mittelpunkt. Frauenkirche, Viktualienmarkt und die Fußgängerzone liegen nur wenige Schritte entfernt – mit der S-Bahn ab Gauting in rund 25 Minuten erreichbar.',
    image: '/images/ausflugsziele/marienplatz_belebt.jpg',
    mapLink: 'https://maps.google.com/?q=Marienplatz+München',
  },
  {
    id: 15,
    world: 'muenchen',
    title: 'Münchens Altstadt erleben',
    tagline: 'Historische Plätze und lebendige Gassen',
    duration: '25 Min.',
    mood: '🏛️ Klassiker',
    tip: 'Tipp: Frühmorgens ist die Altstadt am schönsten – ohne Menschenmassen.',
    highlights: ['Historische Plätze', 'Fassaden', 'Gassen', 'Atmosphäre'],
    description: 'Historische Plätze, schöne Fassaden und lebendige Gassen machen die Altstadt zu einem Klassiker für einen gelungenen Tag in München.',
    image: '/images/ausflugsziele/Muenchen_Altstadt.jpg',
    mapLink: 'https://maps.google.com/?q=München+Altstadt',
  },
  {
    id: 16,
    world: 'muenchen',
    title: 'Über den Viktualienmarkt schlendern',
    tagline: 'Farben, Düfte und Lebensgefühl',
    duration: '25 Min.',
    mood: '🥨 Genussmomente',
    tip: 'Tipp: Probieren Sie die frischen Obatzda-Stände – ein bayerischer Klassiker!',
    highlights: ['Markt', 'Genuss', 'Atmosphäre', 'Münchner Lebensgefühl'],
    description: 'Farben, Düfte und Münchner Lebensgefühl: Der Viktualienmarkt ist ideal für einen entspannten Stadtbummel mit Genussmomenten.',
    image: '/images/ausflugsziele/viktualienmarkt_muenchen_gemuesestand_mit_radi.jpg',
    mapLink: 'https://maps.google.com/?q=Viktualienmarkt+München',
  },
  {
    id: 18,
    world: 'muenchen',
    title: 'Im Englischen Garten spazieren',
    tagline: 'Grün, weit und entspannt',
    duration: '30 Min.',
    mood: '🌳 Stadtbesuch mit Luft',
    tip: 'Tipp: Der Chinesische Turm ist ein schöner Zwischenstopp mit Biergarten.',
    highlights: ['Grün', 'Weite', 'Entspannung', 'Mitten in München'],
    description: 'Viel Grün, viel Weite und mitten in München doch ganz entspannt – perfekt für einen Stadtbesuch mit Luft und Leichtigkeit.',
    image: '/images/ausflugsziele/englischer_garten.jpg',
    objectPosition: 'center top',
    mapLink: 'https://maps.google.com/?q=Englischer+Garten+München',
  },
  {
    id: 20,
    world: 'berge',
    title: 'Bayerische Alpen',
    tagline: 'Wenn es größer sein darf',
    duration: '60 Min.',
    mood: '⛰️ Gipfelglück',
    tip: 'Tipp: Zugspitze mit der Zahnradbahn – Deutschlands höchster Punkt.',
    highlights: ['Zugspitze', 'Garmisch', 'Wandern', 'Skifahren'],
    description: 'Eine Stunde Fahrt, eine andere Welt. Die Alpen bieten im Sommer Wanderparadiese, im Winter Skigebiete – und das ganze Jahr über Panoramen, die sprachlos machen.',
    image: '/images/ausflugsziele/Ausflugsziele_Benediktenwang_Gipfelkreuz.jpg',
    objectPosition: 'center calc(50% - 100px)',
    objectPositionMobile: 'center calc(50% - 30px)',
    mapLink: 'https://maps.google.com/?q=Zugspitze+Garmisch',
  },
  {
    id: 21,
    world: 'muenchen',
    title: 'Pinakotheken München',
    tagline: 'Weltklasse-Kunst, 25 Min. entfernt',
    duration: '25 Min. S-Bahn',
    mood: '🎨 Kunst & Kaffee',
    tip: 'Tipp: Sonntags kostet der Eintritt nur 1 € – kein Witz.',
    highlights: ['Alte Pinakothek', 'Moderne Kunst', 'Museumsshop', 'Café'],
    description: 'Drei Pinakotheken, ein Kunstareal. Von Rubens bis Warhol – München hat eine der dichtesten Museumslandschaften Europas. Und das alles in 25 Minuten erreichbar.',
    image: '/images/ausflugsziele/pinakothek.jpg',
    mapLink: 'https://maps.google.com/?q=Pinakothek+München',
    link: 'https://www.pinakothek.de/de/programm?audiance=all&location=all&format=all',
    linkLabelKey: 'linkProgramPinakothek',
  },
  {
    id: 22,
    world: 'kultur',
    title: 'Buchheim Museum besuchen',
    tagline: 'Kunst, Architektur und Seelage',
    duration: '25 Min.',
    mood: '🎨 Inspirierende Stunden',
    tip: 'Tipp: Das Museum liegt direkt am Starnberger See – perfekt für eine Kombination mit Spaziergang.',
    highlights: ['Kunst', 'Architektur', 'Seelage', 'Kultur'],
    description: 'Kunst, Architektur und Seelage treffen hier aufeinander – ein besonderer Kulturort für inspirierende Stunden drinnen.',
    image: '/images/ausflugsziele/Ausflugsziele_Buchheim_Museum_neu.jpg',
    objectPosition: 'center calc(50% - 50px)',
    mapLink: 'https://maps.google.com/?q=Buchheim+Museum+Starnberger+See',
    link: 'https://www.buchheimmuseum.de/',
    linkLabelKey: 'linkProgramBuchheim',
  },
  {
    id: 23,
    world: 'kultur',
    title: 'Museum Starnberger See entdecken',
    tagline: 'Geschichte und Seeleben',
    duration: '20 Min.',
    mood: '🏛️ Anschaulich & charmant',
    tip: 'Tipp: Das Museum zeigt auch die Geschichte von König Ludwig II. – sehr sehenswert.',
    highlights: ['Geschichte', 'Alltag', 'Seeleben', 'Regional'],
    description: 'Wer die Region besser kennenlernen will, findet hier Geschichte, Alltag und Seeleben auf anschauliche und charmante Weise.',
    image: '/images/ausflugsziele/Museum_Starnberger_See.jpg',
    objectPosition: 'center calc(50% - 120px)',
    credit: 'Boschfoto, CC BY-SA 3.0, via Wikimedia Commons',
    creditUrl: 'https://creativecommons.org/licenses/by-sa/3.0',
    mapLink: 'https://maps.google.com/?q=Museum+Starnberger+See',
  },
  {
    id: 24,
    world: 'muenchen',
    title: 'Museen, Ausstellungen oder Kultur in München genießen',
    tagline: 'Große Museen und schöne Abende',
    duration: '25 Min.',
    mood: '🎭 Kulturgenuss',
    tip: 'Tipp: Das Deutsche Museum ist das größte Technikmuseum der Welt – ein ganzer Tag reicht kaum.',
    highlights: ['Museen', 'Ausstellungen', 'Kultur', 'Abendprogramm'],
    description: 'Wenn es etwas mehr Kultur sein darf, bietet München große Museen, Ausstellungen und viele schöne Abende in der Stadt.',
    image: '/images/ausflugsziele/museen_muenchen.jpg',
    mapLink: 'https://maps.google.com/?q=Museen+München',
  },
  {
    id: 25,
    world: 'gauting',
    title: 'Einen Film im Breitwand Gauting sehen',
    tagline: 'Kinoabend mit Atmosphäre',
    duration: '5 Min.',
    mood: '🎬 Entspannter Abend',
    tip: 'Tipp: Das Breitwand ist ein charmantes Programmkino mit guter Filmauswahl.',
    highlights: ['Kino', 'Atmosphäre', 'Nah', 'Abendprogramm'],
    description: 'Ein Kinoabend mit angenehmer Atmosphäre und guter Auswahl – ruhig, nah und genau richtig für einen entspannten Abend.',
    image: '/images/ausflugsziele/breitwand_kino.jpg',
    mapLink: 'https://maps.google.com/?q=Breitwand+Gauting',
  },
  {
    id: 26,
    world: 'gauting',
    title: 'Würmtal Wanderweg: zum Grubmühlerfeld',
    tagline: 'Wasser, Bäume und Ruhe',
    duration: 'Direkt',
    mood: '🌲 Vor der Tür',
    tip: 'Tipp: Das Mühltal ist besonders im Herbst wunderschön – mit bunten Blättern und stillen Wegen.',
    highlights: ['Würmtal', 'Grubmühlerfeld', 'Ruhe', 'Natur'],
    description: 'Wasser, Bäume und viel Ruhe: Der Würmtal-Wanderweg zum Grubmühlerfeld führt direkt vom Hotel in eine der schönsten Naturlandschaften der Umgebung.',
    image: '/images/ausflugsziele/Ausflug_Gauting_Wuermtal (2).jpg',
    mapLink: 'https://maps.google.com/?q=Würmtal+Gauting',
  },
  {
    id: 27,
    world: 'gauting',
    title: 'Einen ruhigen Tag direkt im Ort verbringen',
    tagline: 'Tage ohne großes Programm',
    duration: 'Direkt',
    mood: '☕ Gelassenheit',
    tip: 'Tipp: Gauting hat kleine Cafés und Läden – perfekt für einen entspannten Vormittag.',
    highlights: ['Kleine Wege', 'Gelassenheit', 'Entspannung', 'Ortsleben'],
    description: 'Gauting eignet sich wunderbar für Tage ohne großes Programm – mit kleinen Wegen, Gelassenheit und entspanntem Rhythmus.',
    image: '/images/ausflugsziele/Gauting_ruhiger_tag_im_ort.jpg',
    mapLink: 'https://maps.google.com/?q=Gauting',
  },
  {
    id: 28,
    world: 'seen',
    title: 'Eibsee',
    tagline: 'Türkisblaues Wasser unterm Zugspitzmassiv',
    duration: '75 Min.',
    mood: '💎 Atemberaubend schön',
    tip: 'Tipp: Früh losfahren – der Eibsee ist einer der schönsten Seen Deutschlands und entsprechend besucht.',
    highlights: ['Türkisblaues Wasser', 'Zugspitzblick', 'Rundweg', 'Baden'],
    description: 'Türkisblaues Wasser, kleine Inselchen und direkt über ihm das Zugspitzmassiv: Der Eibsee gehört zu den schönsten Seen Deutschlands – und ist in gut einer Stunde erreichbar.',
    image: '/images/ausflugsziele/Ausflugsziele_Eibsee_Inseln_Zugspitze.jpg',
    objectPosition: 'center calc(50% - 50px)',
    mapLink: 'https://maps.google.com/?q=Eibsee+Grainau',
  },
  {
    id: 29,
    world: 'seen',
    title: 'Ostersee',
    tagline: 'Stilles Wasser im Voralpenland',
    duration: '55 Min.',
    mood: '🌅 Ruhe & Natur',
    tip: 'Tipp: Der Rundwanderweg um den Ostersee ist besonders schön – naturbelassen und kaum überlaufen.',
    highlights: ['Rundwanderweg', 'Naturschutzgebiet', 'Ruhe', 'Abendstimmung'],
    description: 'Stilles Wasser, weite Natur und kaum Trubel: Der Ostersee im Voralpenland ist ein Geheimtipp für alle, die Ruhe und unberührte Landschaft suchen.',
    image: '/images/ausflugsziele/Ausflugsziele_Ostersee_Badewiese_Herbst.jpg',
    mapLink: 'https://maps.google.com/?q=Ostersee+Bayern',
  },
  {
    id: 34,
    world: 'seen',
    title: 'Rundweg um den Ostersee',
    tagline: 'Naturbelassener Weg durch Moor und Wald',
    duration: '55 Min.',
    mood: '🚶 Stille Runde',
    tip: 'Tipp: Der Rundweg ist gut ausgeschildert und führt durch ein wunderschönes Naturschutzgebiet.',
    highlights: ['Rundwanderweg', 'Moorlandschaft', 'Wald', 'Ruhe'],
    description: 'Ein naturbelassener Rundweg durch Moor, Wald und direkt am Seeufer – der Ostersee-Rundweg ist ideal für alle, die wandern und dabei echte Stille erleben wollen.',
    image: '/images/ausflugsziele/Ausflug_Ostersee_Rundwanderweg.jpg',
    mapLink: 'https://maps.google.com/?q=Ostersee+Rundwanderweg+Bayern',
  },
  {
    id: 30,
    world: 'seen',
    title: 'Staffelsee',
    tagline: 'Weite Voralpenlandschaft und Murnau',
    duration: '60 Min.',
    mood: '🐄 Voralpenidyll',
    tip: 'Tipp: Kombinieren Sie den See mit einem Besuch im malerischen Murnau am Staffelsee.',
    highlights: ['Weite', 'Voralpenlandschaft', 'Murnau', 'Naturschutz'],
    description: 'Weite Seelagen, Alpenblick und die Nähe zum charmanten Städtchen Murnau machen den Staffelsee zu einem wunderbaren Ausflugsziel in der Voralpenlandschaft.',
    image: '/images/ausflugsziele/Ausflugsziele_Staffelsee_Bergblick.jpg',
    objectPosition: 'center calc(50% - 100px)',
    objectPositionMobile: 'center calc(50% - 30px)',
    mapLink: 'https://maps.google.com/?q=Staffelsee+Murnau',
  },
  {
    id: 31,
    world: 'seen',
    title: 'Midgardhaus Tutzing',
    tagline: 'Historische Villa direkt am Starnberger See',
    duration: '20 Min.',
    mood: '🏡 Seeblick & Geschichte',
    tip: 'Tipp: Der Uferweg entlang des Starnberger Sees in Tutzing lohnt sich für einen schönen Spaziergang.',
    highlights: ['Jugendstil', 'Seeblick', 'Tutzing', 'Uferweg'],
    description: 'Das Midgardhaus in Tutzing ist ein beeindruckendes Jugendstilgebäude direkt am Starnberger See – ein schönes Ziel für einen Spaziergang mit Geschichte und Seeblick.',
    image: '/images/ausflugsziele/Ausflug_Starnberger_See_Tutzing_Midgardhaus.jpg',
    mapLink: 'https://maps.google.com/?q=Midgardhaus+Tutzing+Starnberger+See',
  },
  {
    id: 32,
    world: 'gauting',
    title: 'Würmtal Wanderweg: nach Leutstetten',
    tagline: 'Entlang der Würm ins stille Dorf',
    duration: '10 Min.',
    mood: '🌾 Ländliche Idylle',
    tip: 'Tipp: Der Weg nach Leutstetten führt idyllisch entlang der Würm – ideal für eine entspannte Morgen- oder Abendrunde.',
    highlights: ['Würmtal', 'Leutstetten', 'Ruhe', 'Natur'],
    description: 'Entlang der Würm durch stille Wiesen und Wälder bis ins Dorf Leutstetten – ein wunderbarer Spaziergang direkt vom Hotel aus.',
    image: '/images/ausflugsziele/Ausflugsziele_Wuermtal_Wuerm_im_Herbst.jpg',
    mapLink: 'https://maps.google.com/?q=Leutstetten+Starnberg',
  },

  // ── An den Seen (NEU) ────────────────────────────────────────────────────────
  {
    id: 35,
    world: 'seen',
    title: 'Tegernsee',
    tagline: 'Alpines Flair und bayerische Lebensart',
    duration: '50 Min.',
    mood: '🏔️ Alpen trifft See',
    tip: 'Tipp: Die Seepromenade in Tegernsee-Ort ist besonders schön – mit Blick auf die umliegenden Berge.',
    highlights: ['Seepromenade', 'Alpenpanorama', 'Biergarten', 'Schifffahrt'],
    description: 'Alpines Flair, klares Wasser und bayerische Lebensart: Der Tegernsee verbindet Seeidyll mit Bergpanorama und ist eines der beliebtesten Ausflugsziele Oberbayerns.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Tegernsee+Bayern',
  },
  {
    id: 36,
    world: 'seen',
    title: 'Schliersee',
    tagline: 'Ursprünglich, ruhig, bergumrahmt',
    duration: '55 Min.',
    mood: '🌿 Ursprüngliches Bayern',
    tip: 'Tipp: Vom Schliersee aus lässt sich der Schliersberger Alm über einen kurzen Wanderweg erreichen – lohnt sich! Sehenswert sind außerdem das Markus Wasmeier Freilichtmuseum und die Slyrs Whisky-Brennerei – deren Whisky schenken wir auch bei uns im Haus aus.',
    highlights: ['Ruhe', 'Bergblick', 'Baden', 'Wandern'],
    description: 'Der Schliersee liegt ruhig und ursprünglich inmitten der Voralpen – weniger bekannt als der Tegernsee, dafür umso authentischer und naturbelassener.',
    image: '/images/ausflugsziele/Ausflugsziele_Schliersee_Bank_im_See.jpg',
    objectPosition: 'center calc(50% - 100px)',
    objectPositionMobile: 'center calc(50% - 30px)',
    mapLink: 'https://maps.google.com/?q=Schliersee+Bayern',
  },

  // ── In München (NEU) ─────────────────────────────────────────────────────────
  {
    id: 37,
    world: 'muenchen',
    title: 'Deutsches Museum',
    tagline: 'Das größte Technikmuseum der Welt',
    duration: '25 Min. S-Bahn',
    mood: '🔬 Staunen & Entdecken',
    tip: 'Tipp: Für einen vollen Besuch mindestens einen halben Tag einplanen – das Museum ist riesig.',
    highlights: ['Technik', 'Wissenschaft', 'Interaktiv', 'Für alle'],
    description: 'Das Deutsche Museum auf der Isar-Insel ist das größte Technik- und Naturwissenschaftsmuseum der Welt – mit über 73.000 Exponaten auf elf Etagen.',
    image: '/images/ausflugsziele/deutsches_museum.jpg',
    mapLink: 'https://maps.google.com/?q=Deutsches+Museum+München',
    link: 'https://www.deutsches-museum.de/museumsinsel/ausstellung/alle-ausstellungen',
    linkLabelKey: 'linkProgramDeutschesMuseum',
  },

  // ── In den Bergen (NEU) ──────────────────────────────────────────────────────
  {
    id: 38,
    world: 'berge',
    title: 'Zugspitze',
    tagline: 'Deutschlands höchster Gipfel',
    duration: '80 Min.',
    mood: '⛰️ Gipfelglück',
    tip: 'Tipp: Mit der Zahnradbahn ab Garmisch – oben wartet ein atemberaubendes Panorama über drei Länder.',
    highlights: ['Zahnradbahn', 'Gipfelpanorama', 'Gletscher', 'Höhenrekord'],
    description: 'Mit der Zahnradbahn oder Gondel auf Deutschlands höchsten Gipfel: Die Zugspitze auf 2.962 m bietet Panoramablick über Bayern, Österreich und die Schweiz.',
    image: '/images/ausflugsziele/zugspitze.jpg',
    mapLink: 'https://maps.google.com/?q=Zugspitze+Garmisch',
  },
  {
    id: 39,
    world: 'berge',
    title: 'Wendelstein',
    tagline: 'Gipfelerlebnis mit Zahnradbahn',
    duration: '65 Min.',
    mood: '🚞 Nostalgie & Aussicht',
    tip: 'Tipp: Die älteste Bergzahnradbahn Deutschlands fährt auf den Wendelstein – ein Erlebnis für sich.',
    highlights: ['Zahnradbahn', 'Bergstation', 'Aussicht', 'Gipfelkreuz'],
    description: 'Der Wendelstein bietet eine der schönsten Aussichten der Bayerischen Voralpen – per Zahnradbahn oder Seilbahn komfortabel erreichbar.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Wendelstein+Bayern',
  },
  {
    id: 40,
    world: 'berge',
    title: 'Blomberg',
    tagline: 'Familienfreundlicher Hausberg',
    duration: '60 Min.',
    mood: '🌄 Einfach & schön',
    tip: 'Tipp: Die Sommerrodelbahn am Blomberg ist ein Spaß für die ganze Familie.',
    highlights: ['Wandern', 'Sommerrodelbahn', 'Alm', 'Familienfreundlich'],
    description: 'Der Blomberg bei Bad Tölz ist der ideale Einstiegsberg für Familien – mit Sommerrodelbahn, schönen Wanderwegen und einer gemütlichen Almhütte.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Blomberg+Bad+Tölz',
  },
  {
    id: 41,
    world: 'berge',
    title: 'Partnachklamm',
    tagline: 'Wilde Schlucht direkt bei Garmisch',
    duration: '75 Min.',
    mood: '💦 Naturgewalt erleben',
    tip: 'Tipp: Auch im Winter lohnt sich die Klamm – Eiszapfen und Eisformationen machen sie besonders spektakulär.',
    highlights: ['Schlucht', 'Wasserfall', 'Naturschauspiel', 'Ganzjährig'],
    description: 'Tosende Wasserfälle, enge Felspassagen und wildromantische Natur: Die Partnachklamm bei Garmisch-Partenkirchen ist ein beeindruckendes Naturerlebnis für alle.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Partnachklamm+Garmisch',
  },
  {
    id: 42,
    world: 'berge',
    title: 'Höllentalklamm',
    tagline: 'Dramatische Schlucht am Fuß der Zugspitze',
    duration: '80 Min.',
    mood: '🪨 Abenteuer & Natur',
    tip: 'Tipp: Festes Schuhwerk und Trittsicherheit sind Pflicht – die Klamm ist anspruchsvoll aber unvergesslich.',
    highlights: ['Schlucht', 'Zugspitzroute', 'Abenteuer', 'Natur'],
    description: 'Die Höllentalklamm ist der klassische Einstieg zur Zugspitze zu Fuß – dramatische Felswände, donnerndes Wasser und pure Wildnis direkt vor Augen.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Höllentalklamm+Grainau',
  },
  {
    id: 43,
    world: 'berge',
    title: 'Hörnle',
    tagline: 'Stiller Gipfel mit großer Aussicht',
    duration: '65 Min.',
    mood: '🏔️ Ruhe am Gipfel',
    tip: 'Tipp: Das Hörnle bei Bad Kohlgrub bietet eine der schönsten Panoramaaussichten auf die Ammergauer Alpen.',
    highlights: ['Wanderung', 'Gipfelpanorama', 'Stille', 'Ammergauer Alpen'],
    description: 'Das Hörnle ist ein stiller, wunderbarer Gipfel – nicht überlaufen, dafür mit einer der schönsten Aussichten auf die Ammergauer Alpen und ins Voralpenland.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Hörnle+Bad+Kohlgrub',
  },
  {
    id: 44,
    world: 'berge',
    title: 'Wallberg',
    tagline: 'Aussichtsberg über dem Tegernsee',
    duration: '55 Min.',
    mood: '🌅 Panoramafrühstück',
    tip: 'Tipp: Mit der Seilbahn bequem hinauf – oben gibt es eine bewirtschaftete Alm mit Panoramablick.',
    highlights: ['Seilbahn', 'Tegernseeblick', 'Alm', 'Sonnenaufgang'],
    description: 'Der Wallberg thront über dem Tegernsee und bietet eine der schönsten Aussichten der Region – per Seilbahn schnell erreichbar, oben wartet die bewirtschaftete Wallbergalm.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Wallberg+Tegernsee',
  },
  {
    id: 45,
    world: 'berge',
    title: 'Brauneck',
    tagline: 'Hausberg von Lenggries mit weiter Aussicht',
    duration: '65 Min.',
    mood: '🎿 Sommer & Winter',
    tip: 'Tipp: Im Sommer ein wunderbares Wandergebiet, im Winter ein beliebtes Skigebiet für die ganze Familie.',
    highlights: ['Gondel', 'Wandern', 'Skifahren', 'Almen'],
    description: 'Das Brauneck bei Lenggries ist ganzjährig ein lohnenswertes Ausflugsziel – mit Gondelbahn, schönen Almhütten und einem weiten Blick über die Voralpen.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Brauneck+Lenggries',
  },

  // ── Kultur & Geschichte (NEU) ────────────────────────────────────────────────
  {
    id: 46,
    world: 'kultur',
    title: 'Kloster Ettal',
    tagline: 'Barocke Pracht im Ammertal',
    duration: '65 Min.',
    mood: '🏛️ Stille & Besinnung',
    tip: 'Tipp: Der klostereigene Käse und der Ettaler Klosterlikör sind bekannte Spezialitäten – direkt im Klosterladen erhältlich.',
    highlights: ['Barockkirche', 'Klosterladen', 'Bergpanorama', 'Geschichte'],
    description: 'Das Benediktinerkloster Ettal liegt eingebettet in die Ammergauer Alpen – mit seiner beeindruckenden Barockkirche, einem eigenen Brauhaus und klösterlicher Stille.',
    image: '/images/ausflugsziele/Ausflugsziele_Ettal_Kirche_außen.jpg',
    mapLink: 'https://maps.google.com/?q=Kloster+Ettal',
  },
  {
    id: 54,
    world: 'kultur',
    title: 'Kraftort Petersberg',
    tagline: 'Basilika auf einem der heiligen Berge Bayerns',
    duration: '60 Min.',
    mood: '🙏 Stille & Spiritualität',
    tip: 'Tipp: Der Aufstieg zur Basilika ist kurz und führt durch eine wunderbare Voralpenlandschaft – ein echter Kraftort.',
    highlights: ['Basilika', 'Kraftort', 'Panoramablick', 'Stille'],
    description: 'Der Petersberg gehört zu den ältesten Wallfahrtsorten Bayerns – ein stiller Kraftort mit romanischer Basilika und weitem Blick über die Voralpenlandschaft.',
    image: '/images/ausflugsziele/Ausflugsziele_Kraftort_Petersberg_Basilika.jpg',
    mapLink: 'https://maps.google.com/?q=Petersberg+Basilika+Bayern',
  },
  {
    id: 47,
    world: 'kultur',
    title: 'Kloster Benediktbeuern',
    tagline: 'Eines der ältesten Klöster Bayerns',
    duration: '50 Min.',
    mood: '📜 Geschichte & Ruhe',
    tip: 'Tipp: Das Kloster beherbergt eine sehenswerte Glashütte und ein Museum zur Klostergeschichte.',
    highlights: ['Barockkirche', 'Klostermuseum', 'Glashütte', 'Geschichte'],
    description: 'Das Kloster Benediktbeuern gehört zu den ältesten Klöstern Bayerns – mit einer beeindruckenden Barockkirche, einem Museum und der einzigartigen historischen Glashütte.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Kloster+Benediktbeuern',
  },
  {
    id: 48,
    world: 'kultur',
    title: 'Schloss Linderhof',
    tagline: 'Ludwigs kleinster, aber liebster Palast',
    duration: '70 Min.',
    mood: '👑 Königliche Träume',
    tip: 'Tipp: Die Parkanlage mit Venusgrotte und Maurischem Kiosk ist ebenso sehenswert wie das Schloss selbst.',
    highlights: ['Schloss', 'Venusgrotte', 'Parkanlage', 'König Ludwig II.'],
    description: 'Schloss Linderhof war das einzige Schloss, das König Ludwig II. vollenden konnte – umgeben von einer traumhaften Parkanlage mit Venusgrotte, Wasserspielen und exotischen Pavillons.',
    image: '/images/ausflugsziele/linderhof.jpg',
    mapLink: 'https://maps.google.com/?q=Schloss+Linderhof',
  },
  {
    id: 49,
    world: 'kultur',
    title: 'Schloss Neuschwanstein',
    tagline: 'Das Märchenschloss der Welt',
    duration: '90 Min.',
    mood: '🏰 Märchenhaft',
    tip: 'Tipp: Tickets unbedingt vorab online buchen – Warteschlangen können sehr lang sein.',
    highlights: ['Schloss', 'Bergkulisse', 'König Ludwig II.', 'Alpseeblick'],
    description: 'Das wohl bekannteste Schloss der Welt thront über dem Alpsee – König Ludwigs Traumschloss ist in rund 90 Minuten erreichbar und ein unvergessliches Erlebnis.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Schloss+Neuschwanstein',
  },
  {
    id: 50,
    world: 'kultur',
    title: 'Oberammergau',
    tagline: 'Lüftlmalerei, Holzschnitzerei & Passionsspiele',
    duration: '70 Min.',
    mood: '🎭 Kultur & Handwerk',
    tip: 'Tipp: Die bemalten Häuser im Ortskern sind ein einzigartiges Kunstwerk – ideal für einen ruhigen Spaziergang.',
    highlights: ['Lüftlmalerei', 'Holzschnitzerei', 'Passionsspiele', 'Alpencharme'],
    description: 'Oberammergau ist weltbekannt für seine Passionsspiele und einzigartige Lüftlmalerei – ein Dorf voller Kunsthandwerk, Geschichte und alpiner Atmosphäre.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Oberammergau',
  },
  {
    id: 51,
    world: 'kultur',
    title: 'Murnau am Staffelsee',
    tagline: 'Malerstadt mit Bergblick',
    duration: '60 Min.',
    mood: '🎨 Kunst & Atmosphäre',
    tip: 'Tipp: Das Schlossmuseum Murnau zeigt Werke des Blauen Reiters – Kandinsky und Münter lebten hier.',
    highlights: ['Altstadt', 'Schlossmuseum', 'Blaue Reiter', 'Bergpanorama'],
    description: 'Murnau ist eine charmante Kleinstadt mit malerischer Altstadt, dem Schlossmuseum mit Werken des Blauen Reiters und einem beeindruckenden Panoramablick auf die Alpen.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Murnau+am+Staffelsee',
  },

  // ── Direkt in Gauting (NEU) ──────────────────────────────────────────────────
  {
    id: 52,
    world: 'gauting',
    title: 'Villenkolonie Gauting',
    tagline: 'Historische Villen aus der Gründerzeit',
    duration: 'Direkt',
    mood: '🏡 Stille Eleganz',
    tip: 'Tipp: Ein Spaziergang durch die Villenstraßen vermittelt einen schönen Eindruck des alten Gauting.',
    highlights: ['Gründerzeit', 'Architektur', 'Ruhe', 'Geschichte'],
    description: 'Gautings Villenkolonie entstand Ende des 19. Jahrhunderts als Sommerfrische für Münchener Bürger – ein stiller Spaziergang durch die historischen Straßen lohnt sich.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Villenkolonie+Gauting',
  },
  {
    id: 53,
    world: 'gauting',
    title: 'Alter Römerort Gauting',
    tagline: 'Antike Geschichte vor der Haustür',
    duration: 'Direkt',
    mood: '🏺 Geschichte hautnah',
    tip: 'Tipp: Die Infotafeln im Ort erklären die Geschichte des römischen Vicus Bratananium.',
    highlights: ['Römerzeit', 'Ausgrabungen', 'Geschichte', 'Lokal'],
    description: 'Gauting liegt auf dem Gebiet des römischen Vicus Bratananium – direkt im Ort finden sich Zeugnisse der antiken Besiedlung, die die Region bis ins 4. Jahrhundert prägten.',
    image: '/images/ausflugsziele/Woerthsee-Rundweg.jpg',
    mapLink: 'https://maps.google.com/?q=Römerort+Gauting',
  },

  // ── Erweiterung (NEU) ────────────────────────────────────────────────────────
  {
    id: 55,
    world: 'kultur',
    title: 'Bad Bayersoien',
    tagline: 'Moor-Heilbad mit alpinem Charme',
    duration: '60 Min.',
    mood: '♨️ Erholung & Stille',
    tip: 'Tipp: Eine Runde um den Soier See verbindet sich wunderbar mit einem Besuch des traditionellen Moorbads.',
    highlights: ['Moorbad', 'Soier See', 'Pfaffenwinkel', 'Erholung'],
    description: 'Bad Bayersoien liegt eingebettet zwischen Moor und Alpenrand im Pfaffenwinkel – ein stiller Ort mit traditionellem Moorheilbad und idyllischem See, der sich für einen entspannten Tagesausflug anbietet.',
    image: '/images/ausflugsziele/bad_bayersoien.jpg',
    mapLink: 'https://maps.google.com/?q=Bad+Bayersoien',
  },
  {
    id: 56,
    world: 'gauting',
    title: 'BOSCO Gauting',
    tagline: 'Kultur direkt vor der Hoteltür',
    duration: 'Direkt',
    mood: '🎭 Kultur vor Ort',
    tip: 'Tipp: Vor der Anreise einen Blick ins BOSCO-Programm werfen – Konzerte, Theater und Lesungen sind oft schnell ausverkauft.',
    highlights: ['Konzerte', 'Theater', 'Lesungen', 'Lokal'],
    description: 'Das BOSCO ist Gautings Kulturzentrum – mit einem abwechslungsreichen Programm aus Konzerten, Theateraufführungen, Lesungen und Filmen. Von uns aus in wenigen Minuten zu Fuß erreichbar.',
    image: '/images/ausflugsziele/bosco_gauting.jpg',
    credit: 'Jakob Klinger Fotografie',
    mapLink: 'https://maps.google.com/?q=BOSCO+Gauting',
    link: 'https://www.bosco-gauting.de/',
    linkLabelKey: 'linkProgramBosco',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

function ExcursionImage({
  images,
  fallback,
  alt,
  isExpanded,
  objectPosition,
  objectPositionMobile,
}: {
  images?: string[];
  fallback: string;
  alt: string;
  isExpanded: boolean;
  objectPosition: string;
  objectPositionMobile?: string;
}) {
  const src = images && images.length > 0 ? images[0] : fallback;
  const scaleClass = isExpanded ? 'scale-105' : 'hover:scale-103';

  return (
    <img
      src={src}
      alt={alt}
      className={`w-full h-full object-cover obj-pos-responsive transition-transform duration-700 ${scaleClass}`}
      style={{
        '--obj-pos': objectPosition,
        ...(objectPositionMobile ? { '--obj-pos-mobile': objectPositionMobile } : {}),
      } as CSSProperties}
    />
  );
}

export default function AusflugszielePage() {
  const [activeWorld, setActiveWorld] = useState<string | null>(null);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [hoveredWorld, setHoveredWorld] = useState<string | null>(null);
  const [mobileShowAll, setMobileShowAll] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const moodRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'de';

  const visibleExcursions = EXCURSIONS.filter((e) => !HIDDEN_EXCURSION_IDS.has(e.id));
  const filtered = activeWorld
    ? visibleExcursions.filter((e) => e.world === activeWorld)
    : visibleExcursions;

  const currentWorld = WORLDS.find((w) => w.id === activeWorld);

  const MOBILE_INITIAL = 5;
  const mobileFiltered = mobileShowAll ? filtered : filtered.slice(0, MOBILE_INITIAL);

  const handleWorldClick = (id: string) => {
    if (id === 'finder') {
      moodRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (activeWorld === id) {
      setActiveWorld(null);
    } else {
      setActiveWorld(id);
      setExpandedCard(null);
      setMobileShowAll(false);
      setTimeout(() => {
        gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  return (
    <>
      <Navigation />

      {/* ── Hero ── */}
      {/* MOBILE: h-[75vh] statt 110vh */}
      <section className="relative h-[75vh] sm:h-[80vh] sm:min-h-[640px] flex items-end sm:items-center justify-center overflow-hidden pb-12 sm:pb-0">
        <div className="absolute inset-0">
          <img
            src="/images/ausflugsziele/oliver-kruse-eF6p25egASI-unsplash.jpg"
            alt="Ausflugsziele rund um Gauting – Starnberger See, München & Bayerische Alpen"
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover object-bottom"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20" />
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 text-left sm:pt-44">
          <div className="max-w-5xl mx-auto text-white">
            <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase mb-3 sm:mb-4 text-amber-300">{t('exHeroLabel')}</p>
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-serif leading-snug sm:leading-tight mb-3 sm:mb-6">
              {t('exHeroTitle').split('\n').map((line, i, arr) => (
                <span key={i} className={i === arr.length - 1 ? 'text-amber-200' : ''}>{line}<br /></span>
              ))}
            </h1>
            <p className="text-sm sm:text-lg text-white/80 max-w-xl leading-relaxed">
              {t('exHeroText')}
            </p>
          </div>
        </div>
      </section>

      {/* ── World Selector ── */}
      <section className="bg-white py-6 sm:py-12 px-4 sm:px-6 border-b border-stone-100">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase text-stone-400 mb-4 sm:mb-8">
            {t('exSelectorPrompt')}
          </p>
          {/* Mobile: 2 Spalten, Desktop: 3 bzw. 6 Spalten */}
          <div className="grid grid-cols-2 gap-2 pb-2 sm:pb-0 sm:grid-cols-3 sm:gap-4 lg:grid-cols-6">
            {WORLDS.map((w) => {
              const isActive = activeWorld === w.id;
              return (
                <button
                  key={w.id}
                  onClick={() => handleWorldClick(w.id)}
                  onMouseEnter={() => setHoveredWorld(w.id)}
                  onMouseLeave={() => setHoveredWorld(null)}
                  className={`relative overflow-hidden rounded-xl sm:rounded-2xl p-3 sm:p-6 flex flex-col items-center gap-1.5 sm:gap-3 transition-all duration-300 cursor-pointer border-2 ${
                    isActive
                      ? `bg-gradient-to-br ${w.color} text-white border-transparent shadow-xl sm:scale-105`
                      : `${w.bg} ${w.border} text-stone-700 hover:shadow-md`
                  }`}
                >
                  <div className={`w-9 h-9 sm:w-14 sm:h-14 flex items-center justify-center rounded-full transition-all duration-300 ${
                    isActive ? 'bg-white/20' : 'bg-white shadow-sm'
                  }`}>
                    <i className={`${w.icon} text-lg sm:text-2xl ${isActive ? 'text-white' : w.accent}`}></i>
                  </div>
                  <span className={`text-xs sm:text-sm font-semibold whitespace-nowrap ${isActive ? 'text-white' : 'text-stone-800'}`}>
                    {t(w.labelKey)}
                  </span>
                  <span className={`text-xs ${isActive ? 'text-white/80' : `${w.accent} opacity-70`}`}>
                    {visibleExcursions.filter((e) => e.world === w.id).length} {t('exCardZielePrefix')}
                  </span>
                </button>
              );
            })}

          </div>

          {activeWorld && (
            <div className="mt-4 sm:mt-6 text-center">
              <button
                onClick={() => setActiveWorld(null)}
                className="text-sm text-stone-400 hover:text-stone-600 transition-colors cursor-pointer underline underline-offset-2"
              >
                {t('exShowAll')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── Cards Grid ── */}
      <section
        ref={gridRef}
        className={`py-8 sm:py-16 px-4 sm:px-6 transition-colors duration-500 ${currentWorld ? currentWorld.bg : 'bg-stone-50'}`}
      >
        <div className="max-w-7xl mx-auto">
          {activeWorld && currentWorld && (
            <div className="mb-6 sm:mb-10 flex items-center gap-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-gradient-to-br ${currentWorld.color}`}>
                <i className={`${currentWorld.icon} text-base sm:text-lg text-white`}></i>
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-serif text-stone-800">{t(currentWorld.labelKey)}</h2>
                <p className="text-stone-500 text-xs sm:text-sm">{filtered.length} {t('exCardCount')}</p>
              </div>
            </div>
          )}

          {!activeWorld && (
            <div className="mb-6 sm:mb-10 text-center">
              <h2 className="text-2xl sm:text-3xl font-serif text-stone-800 mb-1 sm:mb-2">{t('exGridTitleAll')}</h2>
              <p className="text-stone-500 text-xs sm:text-sm">{t('exGridSubtitle')}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
            {/* Mobile: show limited cards; Desktop: show all */}
            {filtered.map((exc, idx) => {
              const world = WORLDS.find((w) => w.id === exc.world)!;
              const isExpanded = expandedCard === exc.id;
              const tKey = (suffix: string) => `ex${exc.id}${suffix}`;
              const tx = (suffix: string, fallback: string) => {
                const key = tKey(suffix);
                const val = t(key);
                return val === key ? fallback : val;
              };
              return (
                <div
                  key={exc.id}
                  className={`bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-400 cursor-pointer border border-stone-100 ${
                    isExpanded ? 'ring-2 ring-amber-400' : ''
                  } ${idx >= MOBILE_INITIAL && !mobileShowAll ? 'hidden sm:block' : ''}`}
                  onClick={() => setExpandedCard(isExpanded ? null : exc.id)}
                >
                  {/* Image */}
                  <div className="relative h-40 sm:h-64 w-full overflow-hidden">
                    <ExcursionImage
                      images={(exc as any).images as string[] | undefined}
                      fallback={exc.image}
                      alt={tx('Title', exc.title)}
                      isExpanded={isExpanded}
                      objectPosition={(exc as any).objectPosition || 'center'}
                      objectPositionMobile={(exc as any).objectPositionMobile}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute top-2 sm:top-4 left-2 sm:left-4 flex gap-2">
                      <span className={`text-xs font-semibold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full ${world.pill}`}>
                        {t(world.labelKey)}
                      </span>
                    </div>
                    <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
                      <span className="text-xs font-medium bg-black/40 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full flex items-center gap-1 backdrop-blur-sm">
                        <i className="ri-time-line"></i>
                        {tx('Duration', exc.duration)}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5">
                      <p className="text-xs text-white/70 mb-0.5 sm:mb-1">{tx('Tagline', exc.tagline)}</p>
                      <h3 className="text-lg sm:text-2xl font-serif text-white">{tx('Title', exc.title)}</h3>
                    </div>
                  </div>

                  {(exc as any).credit && (
                    <div className="px-3 sm:px-6 pt-1.5 pb-0 text-right">
                      {(exc as any).creditUrl ? (
                        <a
                          href={(exc as any).creditUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-[10px] text-stone-400 hover:text-stone-600 transition-colors"
                        >
                          © {(exc as any).credit}
                        </a>
                      ) : (
                        <span className="text-[10px] text-stone-400">© {(exc as any).credit}</span>
                      )}
                    </div>
                  )}
                  {/* Body */}
                  <div className="p-3 sm:p-6">
                    <div className="flex items-center justify-between mb-2 sm:mb-4">
                      <span className="text-xs sm:text-sm font-medium text-stone-500">{tx('Mood', exc.mood)}</span>
                      <button
                        className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
                          isExpanded ? 'bg-amber-100 text-amber-700 rotate-180' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                        }`}
                        onClick={(e) => { e.stopPropagation(); setExpandedCard(isExpanded ? null : exc.id); }}
                      >
                        <i className="ri-arrow-down-s-line text-base sm:text-lg"></i>
                      </button>
                    </div>

                    {/* Highlights */}
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-2 sm:mb-4">
                      {exc.highlights.map((h, hi) => (
                        <span key={h} className={`text-xs px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full font-medium ${world.pill}`}>
                          {tx(`H${hi + 1}`, h)}
                        </span>
                      ))}
                    </div>

                    {/* Expandable content */}
                    <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <p className="text-stone-600 text-sm leading-relaxed mb-3 sm:mb-4">
                        {tx('Desc', exc.description)}
                      </p>
                      <div className={`rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 flex items-start gap-2 sm:gap-3 ${world.bg} border ${world.border}`}>
                        <i className={`ri-lightbulb-line mt-0.5 ${world.accent}`}></i>
                        <p className={`text-xs sm:text-sm font-medium ${world.accent}`}>{tx('Tip', exc.tip)}</p>
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-2 items-center">
                        <a
                          href={exc.mapLink}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-amber-700 hover:text-amber-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-map-pin-2-line"></i>
                          {t('exMapLink')}
                          <i className="ri-external-link-line text-xs"></i>
                        </a>
                        {(exc as any).link && (
                          <a
                            href={(exc as any).link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-stone-500 hover:text-amber-700 transition-colors cursor-pointer"
                          >
                            {(exc as any).linkLabelKey ? t((exc as any).linkLabelKey) : t('exMoreInfo')}
                            <i className="ri-external-link-line text-[10px]"></i>
                          </a>
                        )}
                      </div>
                    </div>

                    {!isExpanded && (
                      <p className="text-xs text-stone-400 mt-1">{t('exExpandPrompt')}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile "Mehr anzeigen" button */}
          {!mobileShowAll && filtered.length > MOBILE_INITIAL && (
            <div className="mt-6 flex justify-center sm:hidden">
              <button
                onClick={() => setMobileShowAll(true)}
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-stone-200 text-stone-700 text-sm font-semibold shadow-sm hover:border-amber-400 hover:text-amber-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-base"></i>
                {t('exShowMore', { count: filtered.length - MOBILE_INITIAL })}
              </button>
            </div>
          )}

          {/* Mobile "Weniger anzeigen" button */}
          {mobileShowAll && filtered.length > MOBILE_INITIAL && (
            <div className="mt-6 flex justify-center sm:hidden">
              <button
                onClick={() => {
                  setMobileShowAll(false);
                  gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-stone-200 text-stone-500 text-sm font-medium shadow-sm hover:border-stone-300 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-up-s-line text-base"></i>
                {t('exShowLess')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-amber-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/ausflugsziele/alpen.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase text-amber-200 mb-3 sm:mb-4">{t('exCtaLabel')}</p>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-serif text-white mb-4 sm:mb-6">
            {t('exCtaTitle')}
          </h2>
          <p className="text-white/80 text-sm sm:text-lg mb-6 sm:mb-8 leading-relaxed">
            {t('exCtaText')}
          </p>
          <button
            onClick={() => navigate(pathFor('rooms', lang))}
            className="bg-white text-amber-700 px-8 sm:px-10 py-3 sm:py-4 rounded-md text-sm sm:text-base font-semibold hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
          >
            {t('exCtaButton')}
          </button>
        </div>
      </section>

      <Footer />
    </>
  );
}

// ─── Mood Finder ─────────────────────────────────────────────────────────────

const MOODS = [
  { qKey: 'moodQ1', options: [
    { labelKey: 'moodQ1Low', value: 'low' },
    { labelKey: 'moodQ1Mid', value: 'mid' },
    { labelKey: 'moodQ1High', value: 'high' },
  ]},
  { qKey: 'moodQ2', options: [
    { labelKey: 'moodQ2Nature', value: 'nature' },
    { labelKey: 'moodQ2Culture', value: 'culture' },
    { labelKey: 'moodQ2Food', value: 'food' },
  ]},
  { qKey: 'moodQ3', options: [
    { labelKey: 'moodQ3Near', value: 'near' },
    { labelKey: 'moodQ3Medium', value: 'medium' },
    { labelKey: 'moodQ3Far', value: 'far' },
  ]},
];

const MOOD_RESULTS: Record<string, { title: string; desc: string; world: string; id: number }[]> = {
  // Entspannt + Natur
  'low-nature-near':    [
    { id: 26, title: 'Spaziergang im Würmtal', desc: 'Wasser, Bäume und viel Ruhe – direkt vor der Hoteltür.', world: 'gauting' },
    { id: 10, title: 'Wörthsee-Rundweg', desc: 'Leichte Bewegung, stille Seemomente – perfekt für einen ruhigen Morgen.', world: 'seen' },
  ],
  'low-nature-medium':  [
    { id: 5,  title: 'Lenné-Park Feldafing', desc: 'Alte Bäume, elegante Ruhe und Seeblick – ein wunderbarer Spaziergang.', world: 'seen' },
    { id: 12, title: 'Weßlinger See', desc: 'Klein, ruhig und unberührt – ideal für einen entspannten Nachmittag am Wasser.', world: 'seen' },
  ],
  'low-nature-far':     [
    { id: 3,  title: 'Roseninsel im Starnberger See', desc: 'Per Zille auf eine stille Insel – Geschichte, Rosen und Ruhe.', world: 'seen' },
    { id: 1,  title: 'Starnberger See', desc: 'Uferpromenade, Eiscafé und weite Sicht – Seele baumeln lassen.', world: 'seen' },
  ],
  // Entspannt + Kultur
  'low-culture-near':   [
    { id: 27, title: 'Ruhiger Tag in Gauting', desc: 'Kleine Wege, Gelassenheit und entspannter Rhythmus direkt im Ort.', world: 'gauting' },
    { id: 25, title: 'Kino im Breitwand Gauting', desc: 'Entspannter Abend mit Atmosphäre – nur 5 Minuten entfernt.', world: 'gauting' },
  ],
  'low-culture-medium': [
    { id: 4,  title: 'Votivkapelle in Berg', desc: 'Stimmungsvoller Spaziergang mit Seeblick – Geschichte am Seeufer.', world: 'kultur' },
    { id: 23, title: 'Museum Starnberger See', desc: 'Geschichte, Alltag und Seeleben auf anschauliche und charmante Weise.', world: 'kultur' },
  ],
  'low-culture-far':    [
    { id: 22, title: 'Buchheim Museum', desc: 'Kunst, Architektur und Seelage – inspirierende Stunden drinnen.', world: 'kultur' },
    { id: 21, title: 'Pinakotheken München', desc: 'Weltklasse-Kunst in drei Häusern – sonniger Museumsbesuch.', world: 'kultur' },
  ],
  // Entspannt + Genuss
  'low-food-near':      [
    { id: 27, title: 'Ruhiger Tag in Gauting', desc: 'Kleine Cafés und Läden – perfekt für einen entspannten Vormittag.', world: 'gauting' },
    { id: 25, title: 'Kino im Breitwand Gauting', desc: 'Entspannter Kinoabend – nur 5 Minuten vom Hotel.', world: 'gauting' },
  ],
  'low-food-medium':    [
    { id: 8,  title: 'Kloster Andechs', desc: 'Heiliger Berg, weltliches Bier und Panorama über den Ammersee.', world: 'kultur' },
    { id: 1,  title: 'Starnberger See', desc: 'Uferpromenade, Bootsverleih und Einkehr – Urlaub im Urlaub.', world: 'seen' },
  ],
  'low-food-far':       [
    { id: 16, title: 'Viktualienmarkt München', desc: 'Farben, Düfte und Münchner Lebensgefühl – entspannt bummeln.', world: 'muenchen' },
    { id: 8,  title: 'Kloster Andechs', desc: 'Klosterbier und bayerische Einkehr auf schönste Weise.', world: 'kultur' },
  ],
  // Mittel + Natur
  'mid-nature-near':    [
    { id: 9,  title: 'Baden & SUP am Wörthsee', desc: 'SUP, Baden und frische Luft – direkt am Seeufer.', world: 'seen' },
    { id: 26, title: 'Spaziergang im Würmtal', desc: 'Direkt ab Hotel durch grüne Wiesen und rauschende Bäche.', world: 'gauting' },
  ],
  'mid-nature-medium':  [
    { id: 2,  title: 'Ammersee', desc: 'Weite Ufer, frische Luft, Fähre nach Andechs.', world: 'seen' },
    { id: 11, title: 'Pilsensee & Schloss Seefeld', desc: 'Wasser, Natur und ein Hauch Kultur kombiniert.', world: 'seen' },
  ],
  'mid-nature-far':     [
    { id: 20, title: 'Bayerische Alpen', desc: 'Zugspitze, Garmisch und Wanderparadiese – eine andere Welt.', world: 'berge' },
    { id: 7,  title: 'Schifffahrt über den Ammersee', desc: 'Weite, Wasser und Sommerfeeling an Bord.', world: 'seen' },
  ],
  // Mittel + Kultur
  'mid-culture-near':   [
    { id: 3,  title: 'Roseninsel besuchen', desc: 'Königliche Atmosphäre – per Zille auf die stille Insel.', world: 'seen' },
    { id: 4,  title: 'Votivkapelle in Berg', desc: 'Geschichte am Seeufer – bewegend und stimmungsvoll.', world: 'kultur' },
  ],
  'mid-culture-medium': [
    { id: 15, title: 'Münchens Altstadt', desc: 'Historische Plätze, schöne Fassaden und lebendige Gassen.', world: 'muenchen' },
    { id: 14, title: 'Schloss Nymphenburg', desc: 'Barockpracht, Kanäle und Parkluft – das Versailles Bayerns.', world: 'muenchen' },
  ],
  'mid-culture-far':    [
    { id: 21, title: 'Pinakotheken München', desc: 'Weltklasse-Kunst in drei Häusern – von Rubens bis Warhol.', world: 'kultur' },
    { id: 24, title: 'Museen & Kultur München', desc: 'Das Deutsche Museum und viele schöne Abende in der Stadt.', world: 'kultur' },
  ],
  // Mittel + Genuss
  'mid-food-near':      [
    { id: 8,  title: 'Kloster Andechs', desc: 'Klosterbier und Panorama – ein Klassiker.', world: 'kultur' },
    { id: 1,  title: 'Starnberger See', desc: 'Uferpromenade, Einkehr und weiter Blick aufs Wasser.', world: 'seen' },
  ],
  'mid-food-medium':    [
    { id: 8,  title: 'Kloster Andechs', desc: 'Heiliger Berg, weltliches Bier – Panorama inklusive.', world: 'kultur' },
    { id: 13, title: 'München Innenstadt', desc: 'Viktualienmarkt, Biergarten, Englischer Garten.', world: 'muenchen' },
  ],
  'mid-food-far':       [
    { id: 16, title: 'Viktualienmarkt München', desc: 'Farben, Düfte und Münchner Lebensgefühl pur.', world: 'muenchen' },
    { id: 18, title: 'Englischer Garten', desc: 'Chinesischer Turm, Biergarten und viel Grün mitten in München.', world: 'muenchen' },
  ],
  // Aktiv + Natur
  'high-nature-near':   [
    { id: 9,  title: 'Baden & SUP am Wörthsee', desc: 'Raus aufs Wasser – SUP-Verleih direkt am Seeufer.', world: 'seen' },
    { id: 26, title: 'Würmtal & Mühltal', desc: 'Einfach losgehen, Kopf frei kriegen – direkt vor der Tür.', world: 'gauting' },
  ],
  'high-nature-medium': [
    { id: 2,  title: 'Ammersee', desc: 'Segeln, Fähre, Naturschutzgebiet – aktiv und ursprünglich.', world: 'seen' },
    { id: 7,  title: 'Schifffahrt Ammersee', desc: 'Weite, Wasser und ein Gefühl von echtem Sommer.', world: 'seen' },
  ],
  'high-nature-far':    [
    { id: 20, title: 'Bayerische Alpen', desc: 'Zugspitze, Wandern, Gipfelglück. Heute ist der Tag dafür.', world: 'berge' },
    { id: 2,  title: 'Ammersee', desc: 'Segeln, Fähre und frischer Fisch – so geht aktiver Urlaub.', world: 'seen' },
  ],
  // Aktiv + Kultur
  'high-culture-near':  [
    { id: 3,  title: 'Roseninsel besuchen', desc: 'Per Zille zur königlichen Insel – Geschichte und Ruhe.', world: 'seen' },
    { id: 4,  title: 'Votivkapelle in Berg', desc: 'Spaziergang mit Seeblick und bewegender Geschichte.', world: 'kultur' },
  ],
  'high-culture-medium':[
    { id: 15, title: 'Münchens Altstadt', desc: 'Historische Plätze, Fassaden und lebendige Gassen erkunden.', world: 'muenchen' },
    { id: 14, title: 'Schloss Nymphenburg', desc: 'Barockpracht, Kanäle und großer Park – das Versailles Bayerns.', world: 'muenchen' },
  ],
  'high-culture-far':   [
    { id: 21, title: 'Pinakotheken München', desc: 'Weltklasse-Kunst, Museumsmeile – ein voller Kulturtag.', world: 'kultur' },
    { id: 24, title: 'Museen & Kultur München', desc: 'Das Deutsche Museum und München auf Entdeckungstour.', world: 'kultur' },
  ],
  // Aktiv + Genuss
  'high-food-near':     [
    { id: 8,  title: 'Kloster Andechs', desc: 'Aufstieg, Klosterkirche, Bier – ein perfekter halber Tag.', world: 'kultur' },
    { id: 1,  title: 'Starnberger See', desc: 'Aktiv ans Wasser, Einkehr und Panorama.', world: 'seen' },
  ],
  'high-food-medium':   [
    { id: 20, title: 'Bayerische Alpen', desc: 'Gipfelglück und Hütteneinkehr – heute ist der richtige Tag.', world: 'berge' },
    { id: 13, title: 'München Innenstadt', desc: 'Energie raus – München wartet mit allem!', world: 'muenchen' },
  ],
  'high-food-far':      [
    { id: 16, title: 'Viktualienmarkt München', desc: 'Volle Dröhnung München – Viktualienmarkt bis Biergarten.', world: 'muenchen' },
    { id: 18, title: 'Englischer Garten', desc: 'Chinesischer Turm, Biergarten und viel Grün mitten in München.', world: 'muenchen' },
  ],
};

function getMoodResults(answers: string[]): { title: string; desc: string; world: string; id: number }[] {
  const key = answers.join('-');
  if (MOOD_RESULTS[key]) return MOOD_RESULTS[key];
  // Fallback basierend auf Energie
  const energy = answers[0];
  if (energy === 'high') return [
    { id: 13, title: 'München Innenstadt', desc: 'Energie raus – München wartet!', world: 'muenchen' },
    { id: 20, title: 'Bayerische Alpen', desc: 'Gipfelglück und Panorama – heute ist der Tag.', world: 'berge' },
  ];
  if (energy === 'low') return [
    { id: 26, title: 'Würmtal & Mühltal', desc: 'Einfach losgehen, Kopf frei kriegen.', world: 'gauting' },
    { id: 1,  title: 'Starnberger See', desc: 'Uferpromenade und Seele baumeln lassen.', world: 'seen' },
  ];
  return [
    { id: 1,  title: 'Starnberger See', desc: 'Immer eine gute Idee.', world: 'seen' },
    { id: 8,  title: 'Kloster Andechs', desc: 'Heiliger Berg und weltliches Bier.', world: 'kultur' },
  ];
}

function MoodFinder() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [results, setResults] = useState<{ title: string; desc: string; world: string; id: number }[] | null>(null);

  const handleAnswer = (val: string) => {
    const newAnswers = [...answers, val];
    if (newAnswers.length === MOODS.length) {
      setResults(getMoodResults(newAnswers));
      setStep(MOODS.length);
    } else {
      setAnswers(newAnswers);
      setStep(step + 1);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers([]);
    setResults(null);
  };

  return (
    <section className="py-12 sm:py-20 px-4 sm:px-6 bg-stone-900 text-white">
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase text-amber-400 mb-3 sm:mb-4">{t('moodKicker')}</p>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif mb-2 sm:mb-3">{t('moodTitle')}</h2>
        <p className="text-stone-400 text-xs sm:text-sm mb-6 sm:mb-10">{t('moodSubtitle')}</p>

        {step < MOODS.length && !results && (
          <div>
            <div className="flex gap-2 justify-center mb-5 sm:mb-8">
              {MOODS.map((_, i) => (
                <div key={i} className={`h-1 rounded-full transition-all duration-300 ${i <= step ? 'bg-amber-400 w-10 sm:w-12' : 'bg-stone-700 w-6 sm:w-8'}`} />
              ))}
            </div>

            <p className="text-lg sm:text-xl font-serif mb-5 sm:mb-8 text-white">{t(MOODS[step].qKey)}</p>
            <div className="flex flex-col gap-2 sm:gap-3">
              {MOODS[step].options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleAnswer(opt.value)}
                  className="w-full py-3 sm:py-4 px-4 sm:px-6 rounded-xl bg-stone-800 hover:bg-amber-700 border border-stone-700 hover:border-amber-600 text-left text-sm font-medium transition-all duration-200 cursor-pointer"
                >
                  {t(opt.labelKey)}
                </button>
              ))}
            </div>
          </div>
        )}

        {results && (
          <div className="flex flex-col gap-4 sm:gap-5">
            <p className="text-stone-300 text-sm mb-1 sm:mb-2">{t('moodResultsLead')}</p>
            {results.map((result, idx) => {
              const world = WORLDS.find((w) => w.id === result.world)!;
              const titleKey = `ex${result.id}Title`;
              const titleVal = t(titleKey);
              const title = titleVal === titleKey ? result.title : titleVal;
              return (
                <div key={idx} className={`rounded-2xl overflow-hidden border-2 ${world.border}`}>
                  <div className={`bg-gradient-to-br ${world.color} p-5 sm:p-7 flex flex-col items-center`}>
                    <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-white/20 mb-3 sm:mb-4">
                      <i className={`${world.icon} text-2xl sm:text-3xl text-white`}></i>
                    </div>
                    <span className="text-white/60 text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase mb-1">
                      {idx === 0 ? t('moodTip1') : t('moodTip2')}
                    </span>
                    <h3 className="text-xl sm:text-2xl font-serif text-white mb-1 sm:mb-2">{title}</h3>
                    <p className="text-white/90 text-sm leading-relaxed">{result.desc}</p>
                  </div>
                  <div className="bg-stone-800 px-6 py-3 sm:py-4 flex justify-center">
                    {(() => {
                        const exc = EXCURSIONS.find(e => e.id === result.id);
                        return (
                          <a
                            href={exc?.mapLink || `https://maps.google.com/?q=${encodeURIComponent(result.title)}+Bayern`}
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-map-pin-2-line"></i>
                            {t('moodMapLink')}
                            <i className="ri-external-link-line text-xs"></i>
                          </a>
                        );
                      })()}
                  </div>
                </div>
              );
            })}
            <button
              onClick={reset}
              className="mt-1 sm:mt-2 px-6 py-2.5 sm:py-3 rounded-lg bg-stone-700 hover:bg-stone-600 text-sm font-medium text-white transition-colors cursor-pointer whitespace-nowrap mx-auto"
            >
              {t('moodReset')}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
